// src/hooks/useContractApi.tsx

import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  ReactNode,
} from 'react'

import type { AwsCollectionResponse } from '@/types/api/aws-response'
import type {
  ContractResource,
  ContractRecord,
  ContractPool,
  ContractId,
} from '@/types/domain/contract'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

type ContractContextValue = {
  data: ContractPool
}

const ContractCtx = createContext<ContractContextValue>({
  data: [],
})

type ContractWritePayload = {
  data: {
    attributes: Partial<ContractRecord>
    relationships: {
      users?: { data: { type: 'user'; id: string }[] }
      sensors?: { data: { type: 'sensor'; id: string }[] }
    }
  }
}

type Props = {
  children: ReactNode
}

export const ContractDataProvider = ({ children }: Props) => {
  const [data, setData] = useState<ContractPool>([])

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      try {
        await refreshToken()
        const res = await fetch(
          `${REACT_APP_API_URL}/contracts/admin/contracts`,
          { headers: authHeader() }
        )
        if (!res.ok) throw res
        const json = await res.json()
        if (!cancelled) setData(json?.data ?? [])
      } catch {
        notifyError('ContractsApi: GET contracts')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <ContractCtx.Provider value={{ data }}>{children}</ContractCtx.Provider>
  )
}

export const useContractPool = (): ContractPool => useContext(ContractCtx).data

export const useContractApi = () => {
  const { start, stop } = usePreloader()

  const pool = useContext(ContractCtx)

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        await refreshToken()
        return await fn()
      } catch (error) {
        const status = error?.status ?? error?.response?.status
        if (status === 401 || status === 403) {
          await refreshToken()
          return await fn()
        }
        notifyError(`contracts request error ${error?.message}`)
        return null
      }
    },
    []
  )

  const withPreloader = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getPool = useCallback(async (): Promise<ContractPool> => {
    if (pool.data.length > 0) return pool.data

    return withPreloader(() =>
      withRequest(async () => {
        const url = `${REACT_APP_API_URL}/contracts/admin/contracts`
        const options = { method: 'GET', headers: { ...authHeader() } }
        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text()
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        const payload =
          (await response.json()) as AwsCollectionResponse<ContractResource>

        return payload.data ?? []
      })
    )
  }, [withRequest, pool, withPreloader])

  type SelectRef = { value: string }

  type ContractFormInput = Partial<
    Pick<
      ContractRecord,
      'name' | 'description' | 'date_begin' | 'date_expire' | 'primary_coach_id'
    >
  > & {
    user_id?: SelectRef[]
    sensor_id?: SelectRef[]
  }

  const save = useCallback(
    (data: ContractFormInput) =>
      withRequest(async () => {
        const {
          date_begin,
          date_expire,
          description,
          name,
          primary_coach_id,
          sensor_id,
          user_id,
        } = data || {}

        if (!name && !description) throw new Error(LC.CONTRACT_NAME_REQUIRED)

        const record: ContractWritePayload = {
          data: {
            attributes: {
              date_begin,
              date_expire,
              description,
              name,
              primary_coach_id,
            },
            relationships: {
              users: {
                data: user_id
                  ? user_id.map(item => ({
                      type: 'user',
                      id: item.value,
                    }))
                  : [],
              },
              sensors: {
                data: sensor_id
                  ? sensor_id.map(item => ({
                      type: 'sensor',
                      id: item.value,
                    }))
                  : [],
              },
            },
          },
        }

        const url = `${REACT_APP_API_URL}/contracts/admin/contracts`
        const options = {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
        }
        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text()
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }
        const result = await response.json()
        notifySuccess(LC.CONTRACT_SAVED)
        return result?.id ?? null
      }),
    [withRequest]
  )

  const update = useCallback(
    (id: ContractId, data: ContractFormInput) =>
      withRequest(async () => {
        const {
          date_begin,
          date_expire,
          description,
          name,
          primary_coach_id,
          sensor_id,
          user_id,
        } = data || {}

        if (!name && !description) throw new Error(LC.CONTRACT_NAME_REQUIRED)

        const record: ContractWritePayload = {
          data: {
            attributes: {
              date_begin,
              date_expire,
              description,
              name,
              primary_coach_id,
            },
            relationships: {
              users: {
                data: user_id
                  ? user_id.map(item => ({
                      type: 'user',
                      id: item.value,
                    }))
                  : [],
              },
              sensors: {
                data: sensor_id
                  ? sensor_id.map(item => ({
                      type: 'sensor',
                      id: item.value,
                    }))
                  : [],
              },
            },
          },
        }

        const url = `${REACT_APP_API_URL}/contracts/admin/contracts/${id}`
        const options = {
          method: 'PATCH',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(record),
        }
        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text()
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }
        notifySuccess(LC.CONTRACT_UPDATED)
        return id
      }),
    [withRequest]
  )

  const remove = useCallback(
    (id: ContractId) =>
      withRequest(async () => {
        if (!id) throw new Error(LC.CONTRACT_ID_REQUIRED)

        const url = `${REACT_APP_API_URL}/contracts/admin/contracts/${id}`
        const options = {
          method: 'DELETE',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
        }
        const response = await fetch(url, options)

        if (!response.ok) {
          const text = await response.text()
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        notifySuccess(LC.CONTRACT_DELETED)

        return true
      }),
    [withRequest]
  )

  return {
    getPool,
    save,
    update,
    remove,
  }
}
